import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Droplets, AlertCircle, Mail, Lock, ChevronDown } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { auth } from '../lib/api'
import { getMsalInstance, MSAL_SCOPES } from '../lib/msalConfig'
import { useAuthStore } from '../store/authStore'
import type { Benutzer } from '../types'

// Microsoft logo as inline SVG
function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showFallback, setShowFallback] = useState(false)
  const [email, setEmail] = useState('')
  const [passwort, setPasswort] = useState('')

  // Handle Microsoft redirect return
  useEffect(() => {
    async function handleRedirect() {
      try {
        setLoading(true)
        const msal = await getMsalInstance()
        const result = await msal.handleRedirectPromise()

        if (result) {
          // Fresh redirect from Microsoft — exchange token
          const tokenRes = await msal.acquireTokenSilent({
            scopes: MSAL_SCOPES,
            account: result.account,
          })
          const portalRes = await auth.microsoftLogin(tokenRes.accessToken)
          login(portalRes.user as Benutzer, portalRes.token)
          navigate('/')
          return
        }

        // No redirect — show login button (don't auto-login silently,
        // user must explicitly click "Mit Microsoft anmelden")
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Microsoft-Anmeldung fehlgeschlagen')
      } finally {
        setLoading(false)
      }
    }
    handleRedirect()
  }, [])

  async function handleMicrosoftLogin() {
    setLoading(true)
    setError('')
    try {
      const msal = await getMsalInstance()

      // If already signed in to Microsoft, try silent token first
      const accounts = msal.getAllAccounts()
      if (accounts.length > 0) {
        try {
          const tokenRes = await msal.acquireTokenSilent({
            scopes: MSAL_SCOPES,
            account: accounts[0],
          })
          const portalRes = await auth.microsoftLogin(tokenRes.accessToken)
          login(portalRes.user as Benutzer, portalRes.token)
          navigate('/')
          return
        } catch {
          // Silent failed → fall through to redirect
        }
      }

      // Full login via redirect
      await msal.loginRedirect({ scopes: MSAL_SCOPES })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Anmeldung fehlgeschlagen')
      setLoading(false)
    }
  }

  async function handleFallbackLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const res = await auth.login(email, passwort)
      login(res.user as Benutzer, res.token)
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-dvh bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm border border-white/20">
            <Droplets size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Vaporex Portal</h1>
          <p className="text-blue-200 text-sm mt-1">Field Management System</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl p-6 shadow-2xl">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">Anmelden</h2>

          {/* Microsoft Button */}
          <button
            onClick={handleMicrosoftLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-2xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all font-semibold text-gray-800 text-sm disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            ) : (
              <MicrosoftLogo />
            )}
            Mit Microsoft anmelden
          </button>

          <p className="text-xs text-gray-400 text-center mt-4">
            Melde dich mit deinem @vaporex.ch Microsoft-Konto an
          </p>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mt-4">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Admin fallback — hidden */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={() => setShowFallback(!showFallback)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mx-auto"
            >
              <ChevronDown size={12} className={`transition-transform ${showFallback ? 'rotate-180' : ''}`} />
              Direktzugang Administrator
            </button>

            {showFallback && (
              <form onSubmit={handleFallbackLogin} className="space-y-3 mt-4">
                <Input
                  label="E-Mail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  icon={<Mail size={15} />}
                  required
                  autoComplete="email"
                />
                <Input
                  label="Passwort"
                  type="password"
                  value={passwort}
                  onChange={(e) => setPasswort(e.target.value)}
                  icon={<Lock size={15} />}
                  required
                  autoComplete="current-password"
                />
                <Button type="submit" fullWidth loading={loading} size="sm" variant="outline">
                  Direkt anmelden
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
